import React, { useEffect, useMemo, useRef, useState } from "react";
import Default from "../../WeekReportInfo/Partials";
import WeekstatenService from "Api/Weekstaten";
import useSWR from "swr";

const collectSelectableIds = (records = [], precheck = false) => {
    const fitems = [];
    const itemsp = [];

    records.forEach((record) => {
        (record.prolonged || []).forEach((row) => {
            if (row.ePAStatus === "open" && precheck) {
                itemsp.push(row.id);
            }
        });

        (Object.values(record.items || {})).forEach((dayBlock) => {
            ["Ochtend", "Middag"].forEach((shift) => {
                const cell = dayBlock?.data?.[shift];
                if (
                    cell &&
                    !cell.bBlock &&
                    cell.bluebox_status === "idle" &&
                    cell.iSubsDateId &&
                    precheck
                ) {
                    fitems.push(cell.iSubsDateId);
                }
            });
        });
    });

    return { fitems, itemsp };
};

const Step3 = ({ data, processData, setStep, formDataState, setProcessData, setOverviewPage }) => {
    const totalWeekstats = processData.iWeekstaatIds.length;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedFitems, setSelectedFitems] = useState([]);
    const [selectedItemsp, setSelectedItemsp] = useState([]);
    const [isFlushing, setIsFlushing] = useState(false);
    const pendingMarkRef = useRef({ fitems: [], itemsp: [] });

    const baseRequest = useMemo(() => ({
        rdApproved: formDataState.rdApproved === 1 ? "Y" : "N",
        rdSalaris: formDataState.rdSalaris === 1 ? "Y" : "N",
        selectCoordinator: formDataState.selectCoordinator ? "on" : "off",
        selectSchool: formDataState.selectSchool ? "on" : "off",
        selectSalarisAdmin: formDataState.selectSalarisAdmin ? "on" : "off",
        selectCoordinatorAttach: formDataState.selectCoordinatorAttach ? "on" : "off",
        selectSchoolAttach: formDataState.selectSchoolAttach ? "on" : "off",
        selectSalarisAdminAttach: formDataState.selectSalarisAdminAttach ? "on" : "off",
        informEmail: formDataState.informEmail,
        informText: (formDataState?.selectSalarisAdmin || formDataState.selectCoordinator || formDataState.selectSchool)
            ? formDataState.informText
            : "",
        dBeginDate: data.dBegin,
        dEndDate: data.dEnd,
        iCommunityId: data.iCommunityId,
    }), [formDataState, data]);

    const requestData = useMemo(() => ({
        ...baseRequest,
        iWeekstaatId: processData.iWeekstaatIds[currentIndex],
    }), [baseRequest, currentIndex, processData.iWeekstaatIds]);

    const {
        data: weeklyListResponse,
        isLoading: showLoader,
        mutate,
    } = useSWR(
        ["weekly-lists", requestData],
        ([, payload]) => {
            const marks = pendingMarkRef.current;
            pendingMarkRef.current = { fitems: [], itemsp: [] };
            return WeekstatenService.getWeeklyLists({
                ...payload,
                fitems: marks.fitems,
                itemsp: marks.itemsp,
            });
        }
    );

    const weekReportData = weeklyListResponse?.data?.data;

    useEffect(() => {
        if (!weekReportData) {
            return;
        }

        const precheck = weekReportData.chckSalaris === "Y";
        const { fitems, itemsp } = collectSelectableIds(weekReportData.records || [], precheck);
        setSelectedFitems(fitems);
        setSelectedItemsp(itemsp);
    }, [weekReportData]);

    const toggleFitem = (checked, id) => {
        setSelectedFitems((prev) => {
            if (checked) {
                return prev.includes(id) ? prev : [...prev, id];
            }
            return prev.filter((selectedId) => selectedId !== id);
        });
    };

    const toggleItemsp = (checked, id) => {
        setSelectedItemsp((prev) => {
            if (checked) {
                return prev.includes(id) ? prev : [...prev, id];
            }
            return prev.filter((selectedId) => selectedId !== id);
        });
    };

    const resetProcessData = () => {
        setProcessData({
            iCommunityId: [],
            iSchoolIds: [],
            iWeekstaatIds: [],
        });
        setOverviewPage(true);
    };

    const handleVerwerken = async () => {
        const markPayload = {
            fitems: selectedFitems,
            itemsp: selectedItemsp,
        };

        if (currentIndex === totalWeekstats - 1) {
            setIsFlushing(true);
            try {
                await WeekstatenService.getWeeklyLists({
                    ...baseRequest,
                    iWeekstaatId: processData.iWeekstaatIds[currentIndex],
                    ...markPayload,
                });
                resetProcessData();
            } catch (error) {
                console.error(error);
            } finally {
                setIsFlushing(false);
            }
            return;
        }

        pendingMarkRef.current = markPayload;
        setSelectedFitems([]);
        setSelectedItemsp([]);
        setCurrentIndex((prev) => prev + 1);
    };

    return (
        <Default
            totalWeekstats={totalWeekstats}
            setCurrentIndex={setCurrentIndex}
            currentIndex={currentIndex}
            weekReportData={weekReportData}
            setStep={setStep}
            showLoader={showLoader || isFlushing}
            setProcessData={setProcessData}
            setOverviewPage={setOverviewPage}
            mutateWeekReport={mutate}
            selectedFitems={selectedFitems}
            selectedItemsp={selectedItemsp}
            toggleFitem={toggleFitem}
            toggleItemsp={toggleItemsp}
            onVerwerken={handleVerwerken}
        />
    );
};

export default Step3;
